import { useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { cveListQuery, type CveListItem } from '../../api/cveSync'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings > Softsee > CVE database: ce e chiar in baza, langa cartonasul de
 * stare. Cautarea (id, descriere, produs sau furnizor) si paginile se fac pe
 * server - baza are sute de mii de randuri, nu se aduce intreaga.
 */

const SEVERITY_LABEL: Record<string, string> = {
  CRITICAL: 'label-danger',
  HIGH: 'label-danger',
  MEDIUM: 'label-warning',
  LOW: 'label-info',
}

function day(value: string | null): string {
  return value ? value.slice(0, 10) : '-'
}

export function CveListPanel() {
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const list = useQuery(cveListQuery(search, page))

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setSearch(draft.trim())
    setPage(1)
  }
  const onClear = () => {
    setDraft('')
    setSearch('')
    setPage(1)
  }

  const data = list.data
  const pages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1

  return (
    <div className="panel panel-default" data-testid="cve-list">
      <div className="panel-heading">
        <strong>Vulnerabilities in the database</strong>
        {data && (
          <span className="text-muted" data-testid="cve-list-total">
            {' '}
            - {data.total.toLocaleString('en-US')} {search !== '' ? 'matching' : 'in total'}
          </span>
        )}
      </div>
      <div className="panel-body">
        <form className="form-inline" onSubmit={onSubmit} role="search" style={{ marginBottom: 10 }}>
          <input
            type="search"
            className="form-control input-sm"
            placeholder="CVE id, description, vendor or product"
            aria-label="Search CVEs"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{ width: 300, maxWidth: '100%' }}
            data-testid="cve-list-search"
          />{' '}
          <button type="submit" className="btn btn-default btn-sm" data-testid="cve-list-search-go">
            <i className="fas fa-search" aria-hidden="true"></i> Search
          </button>
          {search !== '' && (
            <>
              {' '}
              <button type="button" className="btn btn-link btn-sm" onClick={onClear} data-testid="cve-list-clear">
                Clear
              </button>
            </>
          )}
        </form>

        {list.error ? (
          <div className="alert alert-danger" role="alert">
            {list.error.message || 'The list could not be loaded.'}
          </div>
        ) : !data ? (
          <Spinner />
        ) : data.items.length === 0 ? (
          <p className="text-muted" data-testid="cve-list-empty">
            {search !== '' ? `No vulnerability matches "${search}".` : 'There are no vulnerabilities in the database yet.'}
          </p>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-condensed table-striped" style={{ marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th>CVE</th>
                    <th>Severity</th>
                    <th>Published</th>
                    <th>Description</th>
                    {data.has_kev && <th title="CISA Known Exploited Vulnerabilities">KEV</th>}
                    {data.has_epss && <th title="Exploit Prediction Scoring System">EPSS</th>}
                  </tr>
                </thead>
                <tbody style={{ opacity: list.isPlaceholderData ? 0.5 : 1 }}>
                  {data.items.map((item) => (
                    <CveRow key={item.cve_id} item={item} kev={data.has_kev} epss={data.has_epss} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="clearfix" data-testid="cve-list-pager">
              <button
                type="button"
                className="btn btn-default btn-sm"
                disabled={page <= 1 || list.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="cve-list-prev"
              >
                <i className="fas fa-chevron-left" aria-hidden="true"></i> Prev
              </button>{' '}
              <span data-testid="cve-list-page">
                Page {data.page} of {pages.toLocaleString('en-US')}
              </span>{' '}
              <button
                type="button"
                className="btn btn-default btn-sm"
                disabled={page >= pages || list.isFetching}
                onClick={() => setPage((p) => p + 1)}
                data-testid="cve-list-next"
              >
                Next <i className="fas fa-chevron-right" aria-hidden="true"></i>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function CveRow({ item, kev, epss }: { item: CveListItem; kev: boolean; epss: boolean }) {
  const label = SEVERITY_LABEL[item.severity] ?? 'label-default'
  return (
    <tr data-testid="cve-list-row">
      <td style={{ whiteSpace: 'nowrap' }}>
        <a href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(item.cve_id)}`} target="_blank" rel="noopener noreferrer">
          {item.cve_id}
        </a>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {item.score === null ? (
          <span className="text-muted">-</span>
        ) : (
          <span className={`label ${label}`} title={item.cvss_version ? `CVSS v${item.cvss_version}` : undefined}>
            {item.score.toFixed(1)}
            {item.severity !== '' ? ` ${item.severity}` : ''}
          </span>
        )}
      </td>
      <td style={{ whiteSpace: 'nowrap' }} title={item.last_modified ? `Last modified ${item.last_modified}` : undefined}>
        {day(item.published)}
      </td>
      <td title={item.description} style={{ maxWidth: 360 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</div>
      </td>
      {kev && (
        <td style={{ whiteSpace: 'nowrap' }}>
          {item.kev ? (
            <span className="label label-danger" title={item.known_ransomware ? 'Known exploited, used in ransomware' : 'Known exploited'}>
              KEV{item.known_ransomware ? ' + ransomware' : ''}
            </span>
          ) : (
            <span className="text-muted">-</span>
          )}
        </td>
      )}
      {epss && (
        <td style={{ whiteSpace: 'nowrap' }} title={item.epss_percentile !== null ? `Percentile ${(item.epss_percentile * 100).toFixed(1)}%` : undefined}>
          {item.epss === null ? <span className="text-muted">-</span> : `${(item.epss * 100).toFixed(1)}%`}
        </td>
      )}
    </tr>
  )
}
